import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import Backend from 'i18next-http-backend';
import { userApi } from '@/services/api';

// Country to language mapping
export const countryToLanguage: Record<string, string> = {
  // English speaking countries
  US: 'en', GB: 'en', CA: 'en', AU: 'en', NZ: 'en', IE: 'en',
  // Spanish speaking countries
  ES: 'es', MX: 'es', AR: 'es', CO: 'es', PE: 'es', VE: 'es',
  CL: 'es', EC: 'es', GT: 'es', CU: 'es', BO: 'es', DO: 'es',
  HN: 'es', PY: 'es', SV: 'es', NI: 'es', CR: 'es', PA: 'es', UY: 'es',
  // French speaking countries
  FR: 'fr', BE: 'fr', CH: 'fr', LU: 'fr', MC: 'fr',
  // German speaking countries
  DE: 'de', AT: 'de', LI: 'de',
  // Portuguese speaking countries
  BR: 'pt', PT: 'pt', AO: 'pt', MZ: 'pt',
  // Italian
  IT: 'it', SM: 'it', VA: 'it',
  // Dutch
  NL: 'nl',
  // Russian speaking countries
  RU: 'ru', BY: 'ru', KZ: 'ru',
  // Chinese
  CN: 'zh', TW: 'zh', HK: 'zh', MO: 'zh', SG: 'zh',
  // Japanese
  JP: 'ja',
  // Korean
  KR: 'ko',
  // Arabic speaking countries (using ar for all)
  SA: 'ar', AE: 'ar', EG: 'ar', KW: 'ar', QA: 'ar', BH: 'ar',
  OM: 'ar', JO: 'ar', LB: 'ar', SY: 'ar', IQ: 'ar', YE: 'ar',
  // Turkish
  TR: 'tr',
  // Polish
  PL: 'pl',
  // Ukrainian
  UA: 'uk',
  // Hindi/English (India)
  IN: 'en', // Default to English for India
  // Azerbaijani (Azerbaijan)
  AZ: 'tr', // Turkish is the closest supported language for Azerbaijan
  // Additional country mappings
  AF: 'en', // Afghanistan
  AL: 'en', // Albania
  DZ: 'ar', // Algeria
  AD: 'es', // Andorra
  AG: 'en', // Antigua and Barbuda
  AM: 'ru', // Armenia
  AW: 'nl', // Aruba
  BD: 'en', // Bangladesh
  BB: 'en', // Barbados
  BZ: 'en', // Belize
  BJ: 'fr', // Benin
  BT: 'en', // Bhutan
  BW: 'en', // Botswana
  BF: 'fr', // Burkina Faso
  BI: 'fr', // Burundi
  KH: 'en', // Cambodia
  CM: 'fr', // Cameroon
  CV: 'pt', // Cape Verde
  CF: 'fr', // Central African Republic
  TD: 'fr', // Chad
  KM: 'fr', // Comoros
  CG: 'fr', // Congo
  CD: 'fr', // Congo, Democratic Republic
  DJ: 'fr', // Djibouti
  DM: 'en', // Dominica
  TL: 'pt', // East Timor
  GQ: 'es', // Equatorial Guinea
  ER: 'en', // Eritrea
  EE: 'en', // Estonia
  ET: 'en', // Ethiopia
  FJ: 'en', // Fiji
  GA: 'fr', // Gabon
  GM: 'en', // Gambia
  GE: 'en', // Georgia
  GH: 'en', // Ghana
  GD: 'en', // Grenada
  GN: 'fr', // Guinea
  GW: 'pt', // Guinea-Bissau
  GY: 'en', // Guyana
  HT: 'fr', // Haiti
  IS: 'en', // Iceland
  ID: 'en', // Indonesia
  IR: 'en', // Iran
  JM: 'en', // Jamaica
  KE: 'en', // Kenya
  KI: 'en', // Kiribati
  KG: 'ru', // Kyrgyzstan
  LA: 'en', // Laos
  LV: 'en', // Latvia
  LS: 'en', // Lesotho
  LR: 'en', // Liberia
  LY: 'ar', // Libya
  LT: 'en', // Lithuania
  MK: 'en', // North Macedonia
  MG: 'fr', // Madagascar
  MW: 'en', // Malawi
  MY: 'en', // Malaysia
  MV: 'en', // Maldives
  ML: 'fr', // Mali
  MT: 'en', // Malta
  MH: 'en', // Marshall Islands
  MR: 'ar', // Mauritania
  MU: 'fr', // Mauritius
  FM: 'en', // Micronesia
  MD: 'ru', // Moldova
  MN: 'en', // Mongolia
  ME: 'en', // Montenegro
  MA: 'ar', // Morocco
  MM: 'en', // Myanmar
  NA: 'en', // Namibia
  NR: 'en', // Nauru
  NP: 'en', // Nepal
  NG: 'en', // Nigeria
  NO: 'en', // Norway
  PK: 'en', // Pakistan
  PW: 'en', // Palau
  PS: 'ar', // Palestine
  PG: 'en', // Papua New Guinea
  PH: 'en', // Philippines
  RW: 'fr', // Rwanda
  WS: 'en', // Samoa
  ST: 'pt', // Sao Tome and Principe
  SN: 'fr', // Senegal
  RS: 'en', // Serbia
  SC: 'fr', // Seychelles
  SL: 'en', // Sierra Leone
  SK: 'en', // Slovakia
  SI: 'en', // Slovenia
  SB: 'en', // Solomon Islands
  SO: 'ar', // Somalia
  ZA: 'en', // South Africa
  LK: 'en', // Sri Lanka
  SD: 'ar', // Sudan
  SR: 'nl', // Suriname
  SZ: 'en', // Eswatini (Swaziland)
  SE: 'en', // Sweden
  TJ: 'ru', // Tajikistan
  TZ: 'en', // Tanzania
  TH: 'en', // Thailand
  TG: 'fr', // Togo
  TO: 'en', // Tonga
  TT: 'en', // Trinidad and Tobago
  TN: 'ar', // Tunisia
  TM: 'ru', // Turkmenistan
  TV: 'en', // Tuvalu
  UG: 'en', // Uganda
  UZ: 'ru', // Uzbekistan
  VU: 'fr', // Vanuatu
  VN: 'en', // Vietnam
  ZM: 'en', // Zambia
  ZW: 'en'  // Zimbabwe
};

// Get language from country code
export const getLanguageFromCountry = (countryCode: string): string => {
  return countryToLanguage[countryCode.toUpperCase()] || 'en';
};


const languageDetector = {
  type: 'languageDetector',
  async: true, // flags below detection to be async
  detect: (callback: any) => {
    // Check if language was manually selected
    const selectedLang = localStorage.getItem('i18nextLng');
    if (selectedLang) {
      callback(selectedLang);
      return;
    }

    // Try to get user's country from API
    userApi.getStatus()
      .then((status) => {
        if (status.data.data.country) {
          const detectedLang = getLanguageFromCountry(status.data.data.country);
          callback(detectedLang);
        } else {
          callback(null); // No country found, fall back to other detectors
        }
      })
      .catch((error) => {
        console.error('Error fetching user status for language detection:', error);
        callback(null); // API failed, fall back to other detectors
      });

  },
  cacheUserLanguage(lng: string) {
    localStorage.setItem('i18nextLng', lng);
  }
};

i18n
  .use(Backend)
  .use(languageDetector as any)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: ['en', 'es', 'fr', 'de', 'pt', 'it', 'nl', 'ru', 'zh', 'ja', 'ko', 'ar', 'tr', 'pl', 'uk'],

    // Namespaces for better organization
    ns: ['common', 'auth', 'room', 'admin', 'validation', 'errors', 'howitworks'],
    defaultNS: 'common',

    detection: {
      order: ['customDetector', 'localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
    },

    interpolation: {
      escapeValue: false, // React already escapes values
    },

    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
      addPath: '/locales/add/{{lng}}/{{ns}}',
    },

    react: {
      useSuspense: true,
    },

    debug: import.meta.env.DEV, // Enable debug in development
  });

export default i18n;